/**
 * ExpiryBanner
 * ============
 * Persistent top-of-page alert shown when the user's subscription is about to
 * expire, has been canceled, or a payment has failed.
 *
 * Data comes from GET /api/billing/subscription-status (polled once on mount).
 *
 * Conditions that show the banner:
 *   - status === 'past_due'            → payment failed (red)
 *   - cancel_at_period_end === true    → user scheduled cancellation (orange)
 *   - days_until_expiry <= 7           → approaching renewal/expiry (yellow)
 *
 * Nothing is shown for free-plan users or when the subscription is healthy.
 */

import { useEffect, useState } from 'react';
import { Alert, AlertTitle, Button, Box, Collapse, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { billingApi } from '../services/api';

function formatDate(unixTs) {
    if (!unixTs) return '';
    return new Date(unixTs * 1000).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric'
    });
}

/**
 * @param {string|null} userPlan  - Firebase custom claim plan value (e.g. 'free', 'pro', 'pro_inr').
 *   Pass null/undefined if unknown; the component will still fetch.
 *   Free-plan users never have an expiry, so the API call is skipped entirely (M4).
 */
export default function ExpiryBanner({ userPlan }) {
    const [status, setStatus]   = useState(null);
    const [visible, setVisible] = useState(false);
    const navigate              = useNavigate();

    useEffect(() => {
        // M4: free-plan users have no subscription — skip the round-trip entirely.
        const isFree = !userPlan || userPlan === 'free';
        if (isFree) return;

        let cancelled = false;
        billingApi.getSubscriptionStatus()
            .then((data) => {
                if (cancelled) return;
                setStatus(data);
                const show =
                    data.status === 'past_due' ||
                    data.cancel_at_period_end === true ||
                    (data.days_until_expiry !== null && data.days_until_expiry !== undefined && data.days_until_expiry <= 7);
                setVisible(show);
            })
            .catch(() => {/* silently ignore — banner is non-critical */});
        return () => { cancelled = true; };
    }, [userPlan]);

    if (!visible || !status) return null;

    // Determine severity and message
    let severity = 'warning';
    let title    = '';
    let message  = '';
    let cta      = 'Manage Subscription';

    if (status.status === 'past_due') {
        severity = 'error';
        title    = 'Payment Failed';
        message  = 'Your last payment could not be processed. Please update your payment method to keep your plan active. Your account has been temporarily downgraded to the free tier.';
        cta      = 'Update Payment Method';
    } else if (status.cancel_at_period_end) {
        severity = 'warning';
        title    = 'Subscription Cancellation Scheduled';
        message  = `Your plan will be canceled on ${formatDate(status.current_period_end)}. After that date your account reverts to the free plan. Reactivate any time before then to keep your features.`;
        cta      = 'Reactivate Plan';
    } else if (status.days_until_expiry !== null && status.days_until_expiry <= 7) {
        const days = status.days_until_expiry;
        severity = days <= 2 ? 'error' : 'warning';
        title    = days === 0 ? 'Plan Expires Today' : `Plan Renews in ${days} Day${days !== 1 ? 's' : ''}`;
        message  = `Your ${status.plan} plan ${status.cancel_at_period_end ? 'expires' : 'renews'} on ${formatDate(status.current_period_end)}. Make sure your payment details are up to date.`;
        cta      = 'Review Billing';
    }

    return (
        <Collapse in={visible}>
            <Alert
                severity={severity}
                onClose={() => setVisible(false)}
                sx={{
                    borderRadius: 0,
                    borderBottom: '1px solid',
                    borderColor: `${severity}.main`,
                    '& .MuiAlert-message': { width: '100%' },
                }}
                action={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            color={severity}
                            onClick={() => navigate('/app/billing')}
                            sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}
                        >
                            {cta}
                        </Button>
                        <IconButton
                            size="small"
                            aria-label="close"
                            color="inherit"
                            onClick={() => setVisible(false)}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                }
            >
                <AlertTitle sx={{ fontWeight: 700 }}>{title}</AlertTitle>
                {message}
            </Alert>
        </Collapse>
    );
}
